import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Instagram, Youtube } from 'lucide-react';
import './Navbar.css';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavClick = () => {
    setIsOpen(false);
    scrollToTop();
  };

  const navItems = [
    { path: '/', label: 'HOME' },
    { path: '/music', label: 'MUSIC' },
    { path: '/tour', label: 'SHOWS' },
    { path: '/news', label: 'NEWS' },
    { path: '/gallery', label: 'GALLERY' },
    { path: '/about', label: 'ABOUT' },
    { path: '/store', label: 'STORE' },
    { path: '/contact', label: 'CONTACT' },
  ];

  const primaryNavItems = navItems.slice(0, -1);
  const contactNavItem = navItems[navItems.length - 1];

  const socialLinks = [
    {
      icon: Instagram,
      url: 'https://www.instagram.com/the_kults_official/',
      label: 'Instagram',
    },
    {
      icon: Youtube,
      url: 'https://www.youtube.com/@thekultsofficial',
      label: 'YouTube',
    },
  ];

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="nav-container">
        <Link to="/" className="nav-logo" onClick={scrollToTop}>
          <span className="logo-text">The Kults</span>
        </Link>

        <div className="nav-menu">
          {primaryNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
              onClick={handleNavClick}
            >
              {item.label}
            </Link>
          ))}
          <Link
            to={contactNavItem.path}
            className={`nav-link ${location.pathname === contactNavItem.path ? 'active' : ''}`}
            onClick={handleNavClick}
          >
            {contactNavItem.label}
          </Link>
          <div className="nav-social" aria-label="Social media">
            {socialLinks.map((social) => (
              <a
                key={social.label}
                href={social.url}
                className="nav-social-link"
                aria-label={social.label}
                target="_blank"
                rel="noopener noreferrer"
              >
                <social.icon size={18} />
              </a>
            ))}
          </div>
        </div>

        <button 
          className="nav-toggle" 
          onClick={toggleMenu}
          aria-label="Toggle navigation menu"
          type="button"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div className={`mobile-menu ${isOpen ? 'open' : ''}`}>
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`mobile-nav-link ${location.pathname === item.path ? 'active' : ''}`}
            onClick={handleNavClick}
          >
            {item.label}
          </Link>
        ))}
        <div className="mobile-nav-social" aria-label="Social media">
          {socialLinks.map((social) => (
            <a
              key={social.label}
              href={social.url}
              className="mobile-nav-social-link"
              aria-label={social.label}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsOpen(false)}
            >
              <social.icon size={20} />
              {social.label}
            </a>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 